import React from 'react';
import { Button, Checkbox, Form, Header, Icon, Input, Message, Segment, Table } from 'semantic-ui-react';
import TierLabel from '../subcomponents/TierLabel';
import { RECOMMENDATION_TIERS } from '../constants';
import { TIME_OPTIONS } from '../PhoneSystemStep/hoursUtils';
import { DELIVER_OPTIONS, SEQUENCE_STEP_TYPES, UNITS_WITH_TIME } from './messagingUtils';

const FollowUpSequence = ({
    sequence,
    errors,
    warnings,
    explanation,
    trigger,
    triggerOptions,
    templateOptions,
    onChange,
    onTriggerChange,
    canContinue,
    onBack,
    onContinue
}) => {
    const { included, name, description, saturday, sunday, steps } = sequence;

    const updateSteps = (newSteps) => onChange({ ...sequence, steps: newSteps });
    const updateStep = (id, changes) => updateSteps(steps.map(step => step.id === id ? { ...step, ...changes } : step));
    const moveStep = (index, direction) => {
        const newSteps = steps.slice();
        const [step] = newSteps.splice(index, 1);
        newSteps.splice(index + direction, 0, step);
        updateSteps(newSteps);
    }
    const addStep = () => updateSteps([...steps, { id: `step_custom_${Date.now()}`, type: 'call', deliver: 'days', timeamount: 1, time: 36000, template_id: '' }]);

    const changeType = (step, type) => updateStep(step.id, { type, template_id: templateOptions[type]?.[0]?.value || '' });
    const changeDeliver = (step, deliver) => updateStep(step.id, {
        deliver,
        timeamount: deliver === 'instant' ? '' : (step.timeamount || 1),
        time: UNITS_WITH_TIME.includes(deliver) ? (step.time || 36000) : 0
    });

    return (
        <Segment>
            <Header as='h3'>
                Follow-Up Sequence <TierLabel tier={RECOMMENDATION_TIERS.RECOMMENDED} />
                <Header.Subheader>{explanation}</Header.Subheader>
            </Header>
            <Checkbox
                toggle
                checked={included}
                onChange={(_, { checked }) => onChange({ ...sequence, included: checked })}
                label='Create a follow-up sequence for new leads'
            />
            {included ? (
                <Form noValidate className='Onboarding__sequence'>
                    <Form.Group widths='equal'>
                        <Form.Field error={!!errors.name}>
                            <label>Sequence Name</label>
                            <Input value={name} onChange={(_, { value }) => onChange({ ...sequence, name: value })} />
                            {errors.name && <div className='Onboarding__field-error'>{errors.name}</div>}
                        </Form.Field>
                        <Form.Select
                            label='Start Automatically'
                            value={trigger}
                            options={triggerOptions}
                            onChange={(_, { value }) => onTriggerChange(value)}
                        />
                    </Form.Group>
                    <Form.Field error={!!errors.description}>
                        <label>Description</label>
                        <Input value={description} onChange={(_, { value }) => onChange({ ...sequence, description: value })} />
                        {errors.description && <div className='Onboarding__field-error'>{errors.description}</div>}
                    </Form.Field>
                    <Form.Group inline>
                        <label>Also run on</label>
                        <Form.Checkbox label='Saturday' checked={saturday} onChange={(_, { checked }) => onChange({ ...sequence, saturday: checked })} />
                        <Form.Checkbox label='Sunday' checked={sunday} onChange={(_, { checked }) => onChange({ ...sequence, sunday: checked })} />
                    </Form.Group>

                    <Table compact unstackable className='Onboarding__sequence-steps'>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell width={1}>#</Table.HeaderCell>
                                <Table.HeaderCell width={3}>Action</Table.HeaderCell>
                                <Table.HeaderCell>When</Table.HeaderCell>
                                <Table.HeaderCell>Template</Table.HeaderCell>
                                <Table.HeaderCell width={2} />
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {steps.map((step, index) => {
                                const stepErrors = errors.steps?.[step.id] || {};
                                const needsTime = UNITS_WITH_TIME.includes(step.deliver);
                                return (
                                    <Table.Row key={step.id} error={Object.keys(stepErrors).length > 0}>
                                        <Table.Cell>{index + 1}</Table.Cell>
                                        <Table.Cell>
                                            <Form.Select compact value={step.type} options={SEQUENCE_STEP_TYPES} onChange={(_, { value }) => changeType(step, value)} />
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className='Onboarding__step-timing'>
                                                {step.deliver !== 'instant' && (
                                                    <Input
                                                        size='small'
                                                        type='number'
                                                        min={1}
                                                        className='Onboarding__step-amount'
                                                        value={step.timeamount}
                                                        onChange={(_, { value }) => updateStep(step.id, { timeamount: value === '' ? '' : Number(value) })}
                                                    />
                                                )}
                                                <Form.Select compact value={step.deliver} options={DELIVER_OPTIONS} onChange={(_, { value }) => changeDeliver(step, value)} />
                                                {needsTime && (
                                                    <Form.Select compact value={step.time} options={TIME_OPTIONS} onChange={(_, { value }) => updateStep(step.id, { time: value })} />
                                                )}
                                            </div>
                                            {stepErrors.timing && <div className='Onboarding__field-error'>{stepErrors.timing}</div>}
                                        </Table.Cell>
                                        <Table.Cell>
                                            {step.type === 'call' ? <span className='Onboarding__hint'>Creates a call task</span> : (
                                                <Form.Select
                                                    compact
                                                    placeholder='Choose template'
                                                    value={step.template_id}
                                                    options={templateOptions[step.type] || []}
                                                    error={!!stepErrors.template}
                                                    onChange={(_, { value }) => updateStep(step.id, { template_id: value })}
                                                />
                                            )}
                                            {stepErrors.template && <div className='Onboarding__field-error'>{stepErrors.template}</div>}
                                        </Table.Cell>
                                        <Table.Cell textAlign='right' className='Onboarding__stage-controls'>
                                            <Icon name='arrow up' link={index > 0} disabled={index === 0} onClick={() => index > 0 && moveStep(index, -1)} aria-label='Move step up' />
                                            <Icon name='arrow down' link={index < steps.length - 1} disabled={index === steps.length - 1} onClick={() => index < steps.length - 1 && moveStep(index, 1)} aria-label='Move step down' />
                                            <Icon name='trash alternate outline' color='red' link={steps.length > 1} disabled={steps.length === 1} onClick={() => steps.length > 1 && updateSteps(steps.filter(({ id }) => id !== step.id))} aria-label='Remove step' />
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                    <Button basic size='small' type='button' onClick={addStep}><Icon name='plus' /> Add Step</Button>
                    {errors.steps_summary && <Message warning size='small'>{errors.steps_summary}</Message>}
                    {warnings.map(warning => <Message key={warning} info size='small'><Icon name='info circle' />{warning}</Message>)}
                </Form>
            ) : (
                <Message info size='small'>No sequence will be created. You can build one later in Settings → Automation → Sequences.</Message>
            )}
            <div className='Onboarding__footer'>
                <Button basic type='button' onClick={onBack}><Icon name='arrow left' /> Back</Button>
                <Button primary floated='right' type='button' disabled={!canContinue} onClick={onContinue}>
                    Review Messaging Setup <Icon name='arrow right' />
                </Button>
                <div style={{ clear: 'both' }} />
            </div>
        </Segment>
    );
}

export default FollowUpSequence;
